/**
 * Pure-function financial calculators. All return plain numbers/objects.
 * Indian conventions (rupees, monthly compounding for SIP/EMI, annual for others).
 */

// SIP: future value of monthly investment compounding monthly
export function sipFV({ monthly, annualRate, years }) {
  const n = years * 12;
  const r = annualRate / 100 / 12;
  if (r === 0) return monthly * n;
  const fv = monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  return Math.round(fv);
}

export function sipReport(params) {
  const fv = sipFV(params);
  const invested = params.monthly * params.years * 12;
  return { fv, invested, gain: fv - invested };
}

// Lumpsum: A = P(1+r)^t
export function lumpsumFV({ principal, annualRate, years }) {
  const r = annualRate / 100;
  return Math.round(principal * Math.pow(1 + r, years));
}

export function lumpsumReport(params) {
  const fv = lumpsumFV(params);
  return { fv, invested: params.principal, gain: fv - params.principal };
}

// Goal: monthly SIP needed to reach a target after inflation adjustment
export function goalSIP({ targetToday, years, inflation = 6, annualRate = 12 }) {
  const targetFuture = targetToday * Math.pow(1 + inflation / 100, years);
  const n = years * 12;
  const r = annualRate / 100 / 12;
  if (r === 0) return Math.round(targetFuture / n);
  const monthly = (targetFuture * r) / ((Math.pow(1 + r, n) - 1) * (1 + r));
  return {
    monthly: Math.round(monthly),
    futureTarget: Math.round(targetFuture),
    totalInvest: Math.round(monthly * n),
  };
}

// Retirement: corpus needed at retirement
export function retirementPlan({
  currentAge, retireAge, monthlyExpenseToday, lifeExpectancy = 85,
  inflation = 6, preReturn = 12, postReturn = 7,
}) {
  const yearsToRetire = retireAge - currentAge;
  const yearsInRetirement = lifeExpectancy - retireAge;
  const expenseAtRetirement = monthlyExpenseToday * Math.pow(1 + inflation / 100, yearsToRetire);
  // Real return post retirement (Fisher approx.)
  const realPostReturn = (1 + postReturn / 100) / (1 + inflation / 100) - 1;
  const monthlyReal = realPostReturn / 12;
  const nMonths = yearsInRetirement * 12;
  let corpusNeeded;
  if (monthlyReal === 0) corpusNeeded = expenseAtRetirement * nMonths;
  else corpusNeeded = expenseAtRetirement * ((1 - Math.pow(1 + monthlyReal, -nMonths)) / monthlyReal);

  // Monthly SIP needed to reach corpus
  const nAcc = yearsToRetire * 12;
  const rAcc = preReturn / 100 / 12;
  let sip;
  if (rAcc === 0) sip = corpusNeeded / nAcc;
  else sip = (corpusNeeded * rAcc) / ((Math.pow(1 + rAcc, nAcc) - 1) * (1 + rAcc));

  return {
    yearsToRetire,
    yearsInRetirement,
    expenseAtRetirement: Math.round(expenseAtRetirement),
    corpusNeeded: Math.round(corpusNeeded),
    monthlySIP: Math.round(sip),
  };
}

// Children's education
export function educationPlan({ childAge, admitAge = 18, costToday, inflation = 8, annualRate = 12 }) {
  const years = admitAge - childAge;
  const futureCost = costToday * Math.pow(1 + inflation / 100, years);
  const n = years * 12;
  const r = annualRate / 100 / 12;
  let sip;
  if (r === 0) sip = futureCost / n;
  else sip = (futureCost * r) / ((Math.pow(1 + r, n) - 1) * (1 + r));
  return {
    years,
    futureCost: Math.round(futureCost),
    monthlySIP: Math.round(sip),
  };
}

// EMI: standard amortization
export function emiCalc({ principal, annualRate, years }) {
  const n = years * 12;
  const r = annualRate / 100 / 12;
  let emi;
  if (r === 0) emi = principal / n;
  else emi = (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const total = emi * n;
  return {
    emi: Math.round(emi),
    totalPayable: Math.round(total),
    totalInterest: Math.round(total - principal),
  };
}

// Inflation: future cost
export function inflationCalc({ amount, inflation, years }) {
  const future = amount * Math.pow(1 + inflation / 100, years);
  return { future: Math.round(future), erosionPct: Math.round((1 - amount / future) * 100) };
}

// Indian currency formatter
export function formatINR(n) {
  if (n === null || n === undefined || isNaN(n)) return "₹0";
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

export function formatINRShort(n) {
  if (n >= 1e7) return "₹" + (n / 1e7).toFixed(2) + " Cr";
  if (n >= 1e5) return "₹" + (n / 1e5).toFixed(2) + " L";
  if (n >= 1e3) return "₹" + (n / 1e3).toFixed(1) + " K";
  return formatINR(n);
}
